'use client';

import { useState, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import Icon from '@/components/ui/AppIcon';

interface CrisisRiskHeaderProps {
  contactId: string;
  refreshTrigger: number;
}

const CrisisRiskHeader = ({ contactId, refreshTrigger }: CrisisRiskHeaderProps) => {
  const router = useRouter();
  const [loading, setLoading] = useState(true);
  const [riskData, setRiskData] = useState<any>(null);

  useEffect(() => {
    fetchRiskData();
  }, [contactId, refreshTrigger]);

  const fetchRiskData = async () => {
    setLoading(true);
    try {
      const res = await fetch(`/api/crisis-prediction/calculate?contactId=${contactId}`);
      const data = await res.json();
      if (data.success && data.data) {
        setRiskData(data.data);
      }
    } catch (error) {
      console.error('Error fetching crisis risk data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="w-full glass p-8 animate-pulse">
        <div className="h-8 bg-sage-200/30 rounded w-1/3 mb-4"></div>
        <div className="h-16 bg-sage-200/30 rounded w-1/2"></div>
      </div>
    );
  }

  const score = riskData?.crisisRiskScore || 72;
  const riskLevel = riskData?.riskLevel || 'monitor';
  const primaryFactors = riskData?.drivingFactors?.slice(0, 2) || [];

  const getRiskColor = () => {
    if (score <= 30) return 'text-success';
    if (score <= 60) return 'text-yellow-600';
    if (score <= 80) return 'text-[#E8A838]';
    return 'text-error';
  };

  const getRiskBgColor = () => {
    if (score <= 30) return 'bg-success/10';
    if (score <= 60) return 'bg-yellow-500/10';
    if (score <= 80) return 'bg-[#E8A838]/10';
    return 'bg-error/10';
  };

  const getRiskBorderColor = () => {
    if (score <= 30) return 'border-success/30';
    if (score <= 60) return 'border-yellow-500/30';
    if (score <= 80) return 'border-[#E8A838]/30';
    return 'border-error/30';
  };

  const getRiskLabel = () => {
    if (score <= 30) return 'LOW — All Clear';
    if (score <= 60) return 'MONITOR — Watch Closely';
    if (score <= 80) return 'ELEVATED — Monitor Closely';
    return 'CRITICAL — Immediate Action';
  };

  return (
    <div className={`w-full glass border-2 rounded-lg p-6 md:p-8 ${getRiskBorderColor()}`}>
      <div className="flex items-start justify-between mb-6">
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-4">
            <div className={`p-3 rounded-lg ${getRiskBgColor()}`}>
              <Icon name="ExclamationTriangleIcon" size={32} className={getRiskColor()} />
            </div>
            <div>
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground">
                Crisis Risk Score
              </h2>
              <p className={`text-lg font-semibold ${getRiskColor()}`}>
                {getRiskLabel()}
              </p>
            </div>
          </div>
        </div>
        <div className="text-right">
          <div className={`text-5xl md:text-6xl font-heading font-bold ${getRiskColor()}`}>
            {score}
            <span className="text-2xl">/100</span>
          </div>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="mb-6">
        <div className="w-full bg-sage-200/30 rounded-full h-4 overflow-hidden">
          <div
            className={`h-full transition-all duration-500 ${
              score <= 30 ? 'bg-success' :
              score <= 60 ? 'bg-yellow-500' :
              score <= 80 ? 'bg-[#E8A838]' : 'bg-error'
            }`}
            style={{ width: `${score}%` }}
          ></div>
        </div>
      </div>

      {/* Primary Factors */}
      <div className="mb-6">
        <p className="text-foreground font-medium mb-2">
          <span className="font-semibold">Primary factors:</span>{' '}
          {primaryFactors.length > 0 ? (
            primaryFactors.map((f: any, i: number) => (
              <span key={i}>
                {f.factor} {f.change}
                {i < primaryFactors.length - 1 ? ' · ' : ''}
              </span>
            ))
          ) : (
            'Medication compliance dropped this week · Pain trending upward'
          )}
        </p>
      </div>

      {/* View Full Analysis Button */}
      <button
        onClick={() => router.push('/crisis-prediction')}
        className="px-6 py-3 bg-primary text-white rounded-lg font-semibold hover:bg-primary/90 transition-smooth flex items-center gap-2"
      >
        <Icon name="ChartBarIcon" size={20} />
        View Full Analysis
      </button>
    </div>
  );
};

export default CrisisRiskHeader;